import { type IconTypes } from '@procurify/ui'

import { type CurrencyName } from '@webapp/core/entities/external/Currency/CurrencyName'
import { type CatalogItemTagVariant } from '../../components/CatalogItemTag'
import { type ConversationTextVariant } from '../../components/ConversationText'
import {
  type ConversationComponentTypes,
  type ConversationItemKinds,
  type ConversationItemKindEnum,
} from '../enums'

export type ConversationMessageSender = 'user' | 'llm'

export interface ConversationChoiceOption {
  label: string
  value: string
  message: string
}

export interface ConversationMessageComponent {
  type: typeof ConversationComponentTypes.MESSAGE
  sender: ConversationMessageSender
  content: string
  tags?: string[]
}

export interface ConversationConfirmationOptionsComponent {
  type: typeof ConversationComponentTypes.CONFIRMATION_OPTIONS
  options: ConversationChoiceOption[]
}

export interface ConversationGroupedDropdownOption {
  id: number | string
  name: string
}

export interface ConversationGroupedDropdownGroup {
  id: number | string
  name: string
  options: ConversationGroupedDropdownOption[]
}

export interface ConversationGroupedDropdownComponent {
  type: typeof ConversationComponentTypes.GROUPED_DROPDOWN
  groups: ConversationGroupedDropdownGroup[]
  groupLabel: string
  optionLabel: string
  defaultGroupId: Nullable<number | string>
  defaultOptionId: Nullable<number | string>
}

export interface ConversationCartItem {
  id: string
  name: string
  img?: string
  quantity: number
  price: number
}

export interface ConversationCartComponent {
  type: typeof ConversationComponentTypes.CART
  cartId: string
  version: number
  totalItems: number
  total: number
  currency: CurrencyName
  items: ConversationCartItem[]
}

export interface ConversationCatalogItemTag {
  icon: IconTypes
  text: string
  variant?: CatalogItemTagVariant
}

export interface ConversationCatalogItemDetail {
  label: string
  value: string
}

export interface ConversationCatalogItemComponent {
  type: typeof ConversationComponentTypes.CATALOG_ITEM
  itemId: string
  img?: string
  title: string
  body: string
  sku: string
  price: number
  currency: CurrencyName
  tags: ConversationCatalogItemTag[]
  agentAnalysis: string
  catalogDetails: ConversationCatalogItemDetail[]
}

export interface ConversationTextComponent {
  type: typeof ConversationComponentTypes.TEXT
  variant: ConversationTextVariant
  content: string
}

export type ConversationComponent =
  | ConversationMessageComponent
  | ConversationConfirmationOptionsComponent
  | ConversationGroupedDropdownComponent
  | ConversationCartComponent
  | ConversationCatalogItemComponent
  | ConversationTextComponent

export interface ConversationMessageItem {
  id: number
  kind: typeof ConversationItemKinds.MESSAGE
  sender: ConversationMessageSender
  content: string
}

export interface ConversationEnvelopeItem {
  id: number
  kind: typeof ConversationItemKinds.ENVELOPE
  envelopeId: string
  components: ConversationComponent[]
  isAnswered: boolean
}

export interface ConversationErrorItem {
  id: number
  kind: typeof ConversationItemKinds.ERROR
  message: string
}

export type ConversationTurnItem =
  | ConversationMessageItem
  | ConversationEnvelopeItem
  | ConversationErrorItem

export type ConversationTurnItemConstructor =
  | Omit<ConversationMessageItem, 'id'>
  | Omit<ConversationEnvelopeItem, 'id'>
  | Omit<ConversationErrorItem, 'id'>

export const createConversationTurnItem = (
  constructor: ConversationTurnItemConstructor,
  id: number
): ConversationTurnItem => ({ ...constructor, id })

export const isConversationItemKind = <K extends ConversationItemKindEnum>(
  item: ConversationTurnItem,
  kind: K
): item is Extract<ConversationTurnItem, { kind: K }> => item.kind === kind
